import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { AdminOfferDetail, OfferRejectionReasonCode } from '../types/offers';
import OfferPhonePreview from '../components/offers/OfferPhonePreview';
import FactsPanel from '../components/offers/FactsPanel';
import OfferVariantPrices from '../components/offers/OfferVariantPrices';
import PolicyChecklist from '../components/offers/PolicyChecklist';
import DecisionBar from '../components/offers/DecisionBar';
import { decisionError } from '../components/offers/offerReviewModel';
import OfferActivityCard from '../components/offers/OfferActivityCard';
import { editGate, formatDubaiInstant } from '../components/offers/offerEditModel';

const QUEUE_PATH = '/service-approvals?tab=offers';

/** Spec 062 Flow B — `/service-approvals/offers/:id`: preview, facts, policy checklist, decision. */
export default function OfferReviewPage() {
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  /** Set by the edit page after a save. */
  const savedToast = (location.state as { toast?: string } | null)?.toast ?? null;

  const [detail, setDetail] = useState<AdminOfferDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!auth.token || !id) return;
    setLoading(true);
    setLoadError(null);
    try {
      setDetail(await api.getOfferForReview(id, auth.token));
    } catch (err) {
      setLoadError(decisionError(err, 'approve').message);
    } finally {
      setLoading(false);
    }
  }, [auth.token, id]);

  useEffect(() => { load(); }, [load]);

  const decide = async (action: 'approve' | 'reject' | 'suspend', call: () => Promise<unknown>, done: string) => {
    setBusy(true);
    setActionError(null);
    try {
      await call();
      navigate(QUEUE_PATH, { state: { toast: done } });
    } catch (err) {
      const mapped = decisionError(err, action);
      setActionError(mapped.message);
      if (mapped.reload) await load();
    } finally {
      setBusy(false);
    }
  };

  if (loading && !detail) {
    return (
      <div className="admin-loading">
        <div className="admin-loading-spinner"></div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div>
        <Link to={QUEUE_PATH} className="admin-link">← Back to offers</Link>
        <div className="admin-info-banner danger" role="alert" style={{ marginTop: '1rem' }}>
          {loadError ?? 'Could not load this offer.'}
        </div>
      </div>
    );
  }

  const { offer } = detail;
  const token = auth.token!;
  const version = offer.version;
  const gate = editGate(offer);

  return (
    <div className="offer-review">
      <div className="offer-review__header">
        <Link to={QUEUE_PATH} className="admin-link">← Back to offers</Link>
        <div className="offer-review__title-row">
          <h2 className="offer-review__title">{offer.titleEn}</h2>
          {gate.visible && (gate.allowed ? (
            <Link to={`/service-approvals/offers/${offer.id}/edit`} className="admin-btn admin-btn-outline admin-btn-sm" data-testid="offer-edit-link">
              Edit offer
            </Link>
          ) : (
            <span className="offer-review__edit-blocked">
              <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" disabled data-testid="offer-edit-link">Edit offer</button>
              <span className="admin-text-muted" data-testid="offer-edit-hint">{gate.hint}</span>
            </span>
          ))}
        </div>
        <div className="admin-text-muted">{detail.garage.name}</div>
        {offer.adminEditedAt && (
          <div className="admin-text-muted" data-testid="offer-admin-edited">Last edited by an admin on {formatDubaiInstant(offer.adminEditedAt)}</div>
        )}
      </div>

      {savedToast && (
        <div className="admin-info-banner success" role="status" style={{ marginBottom: '1rem' }}>{savedToast}</div>
      )}

      {actionError && (
        <div className="admin-info-banner danger" role="alert" data-testid="offer-action-error" style={{ marginBottom: '1rem' }}>
          {actionError}
        </div>
      )}
      {loadError && (
        <div className="admin-info-banner danger" role="alert" style={{ marginBottom: '1rem' }}>{loadError}</div>
      )}

      <div className="offer-review__grid">
        <div className="offer-review__preview">
          <OfferPhonePreview offer={offer} garageName={detail.garage.name} />
        </div>
        <div className="offer-review__side">
          <DecisionBar
            key={offer.id}
            offer={offer}
            busy={busy || loading}
            onApprove={() => decide('approve', () => api.approveOffer(offer.id, { version }, token), 'Offer approved')}
            onReject={(reasonCode: OfferRejectionReasonCode, reason: string) =>
              decide('reject', () => api.rejectOffer(offer.id, { reasonCode, reason, version }, token), 'Offer rejected')}
            onSuspend={(reason: string) =>
              decide('suspend', () => api.suspendOffer(offer.id, { reason, version }, token), 'Offer suspended')}
          />
          <FactsPanel detail={detail} />
          <OfferVariantPrices offerId={offer.id} version={version} token={token} />
          <PolicyChecklist detail={detail} />
          <OfferActivityCard offerId={offer.id} token={token} refreshKey={version} />
        </div>
      </div>
    </div>
  );
}
